import { BACKEND_URL } from "@/lib/api";
import { useEffect, useMemo, useState } from "react";
import { CalendarDays, X, ChevronRight } from "lucide-react";
import { findCourse, matchesCourse, COURSE_DETAILS } from "../../data/courses";

const API = BACKEND_URL;
const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });

export const slotLabel = (item) => `${fmtDay(item.date)} · ${item.start_time} Uhr · ${findCourse(item.name)?.title || item.name}`;

export const loadTwoWeeks = async () => {
  const week = await fetch(`${API}/api/kursplan`).then((r) => (r.ok ? r.json() : null));
  if (!week) return [];
  const next = await fetch(`${API}/api/kursplan?start=${addDays(week.days[0].date, 7)}`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
  const nowDate = week.today;
  const nowTime = new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });
  return [...week.days, ...(next?.days || [])]
    .map((d) => ({ ...d, items: d.items.filter((i) => i.kind === "course" && !i.canceled && (d.date > nowDate || (d.date === nowDate && i.start_time > nowTime))) }))
    .filter((d) => d.date >= nowDate && d.items.length);
};

export const COURSE_OPTIONS = COURSE_DETAILS.filter((c) => c.category !== "Open-Zeit").map((c) => c.title);

export const CourseChips = ({ value, onChange, prefix = "course-chip", size = "md" }) => (
  <div className="flex flex-wrap gap-2" role="group" aria-label="Kurs wählen">
    {COURSE_OPTIONS.map((c) => <button type="button" key={c} data-testid={`${prefix}-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} aria-pressed={value === c}
      onClick={() => onChange(value === c ? "" : c)}
      className={`rounded-full border font-head font-semibold transition-colors ${size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"} ${value === c ? "bg-[#161616] text-white border-[#161616]" : "bg-white border-[#E6E3DD] hover:border-[#161616]"}`}>{c}</button>)}
  </div>
);

const SelectedSlot = ({ value, onChange, label = "Dein Wunschtermin" }) => (
  <div data-testid="slot-selected" className="flex items-center justify-between gap-3 rounded-xl bg-white border border-[#BA00C5]/40 px-4 py-3">
    <span className="flex items-center gap-2.5 text-sm"><CalendarDays size={16} className="text-[#BA00C5]" /><span><span className="block text-xs muted">{label}</span><span className="font-semibold">{value}</span></span></span>
    <button type="button" data-testid="slot-clear-btn" onClick={() => onChange("", "")} className="p-2 rounded-full hover:bg-[#F1EFEA]" aria-label="Wunschtermin entfernen"><X size={16} /></button>
  </div>
);

export const CourseSlots = ({ course, value, onChange }) => {
  const [days, setDays] = useState(null);
  useEffect(() => { loadTwoWeeks().then(setDays).catch(() => setDays([])); }, []);
  const items = useMemo(() => (days || []).flatMap((d) => d.items.filter((i) => matchesCourse(i.name, course)).map((i) => ({ ...i, date: d.date }))), [days, course]);
  if (value) return <SelectedSlot value={value} onChange={onChange} label="Dein Wunschtermin" />;
  return (
    <div data-testid="course-slots" className="rounded-xl bg-white border border-[#D9D5CE] p-4">
      <p className="text-sm font-semibold font-head">Nächste Termine <span className="font-normal muted">· Tipp auf einen, um ihn vorzumerken</span></p>
      {days === null && <p data-testid="course-slots-loading" className="mt-3 text-sm muted">Kursplan wird geladen …</p>}
      {days && items.length === 0 && <p data-testid="course-slots-empty" className="mt-3 text-sm muted">In den nächsten zwei Wochen steht {course} nicht im Plan – wir melden uns mit passenden Terminen.</p>}
      {items.length > 0 && (
        <><ul className="mt-3 grid sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
          {items.map((i) => (
            <li key={i.id} className="min-w-0">
              <button type="button" data-testid="course-slot-option" onClick={() => onChange(slotLabel(i), i.date)} disabled={i.spots_left === 0}
                className="w-full min-w-0 text-left rounded-lg border border-[#E6E3DD] hover:border-[#BA00C5] disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2.5 flex items-center gap-3 transition-colors">
                <span className="w-1.5 h-8 rounded-full shrink-0" style={{ backgroundColor: i.color }} />
                <span className="min-w-0"><span className="block text-xs font-semibold tabular-nums">{fmtDay(i.date)} · {i.start_time} – {i.end_time}</span><span className="block text-sm font-head font-bold truncate">{findCourse(i.name)?.title || i.name}{i.trainer ? <span className="font-normal muted"> · {i.trainer}</span> : null}</span></span>
                {i.spots_left !== null && i.spots_left !== undefined && <span className={`ml-auto text-xs shrink-0 font-semibold ${i.spots_left === 0 ? "muted" : i.spots_left <= 3 ? "text-[#C2410C]" : "text-[#047857]"}`}>{i.spots_left === 0 ? "voll" : `${i.spots_left} frei`}</span>}
              </button>
            </li>
          ))}
        </ul>
        <p data-testid="course-slots-note" className="mt-3 text-xs muted">Das ist noch keine Buchung – deinen Termin bestätigen wir persönlich per E-Mail.</p></>
      )}
    </div>
  );
};

export const SlotPicker = ({ value, onChange, topic }) => {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(null);
  const [activeDay, setActiveDay] = useState(null);

  useEffect(() => {
    if (!open || days) return;
    loadTwoWeeks().then((all) => { setDays(all); setActiveDay(all[0]?.date || null); }).catch(() => setDays([]));
  }, [open, days]);

  const items = useMemo(() => {
    const d = days?.find((x) => x.date === activeDay);
    if (!d) return [];
    const perf = /hyrox|athx|kraftpur/i;
    return topic === "performance" ? [...d.items].sort((a, b) => (perf.test(b.name) ? 1 : 0) - (perf.test(a.name) ? 1 : 0)) : d.items;
  }, [days, activeDay, topic]);

  if (value) return <SelectedSlot value={value} onChange={onChange} />;

  if (!open) return (
    <button type="button" data-testid="slot-open-btn" onClick={() => setOpen(true)} className="w-full flex items-center justify-between gap-3 rounded-xl bg-white border border-dashed border-[#C9C4BA] hover:border-[#BA00C5] px-4 py-3.5 text-left transition-colors">
      <span className="flex items-center gap-2.5 text-sm"><CalendarDays size={16} className="text-[#BA00C5]" />Wunschtermin aus dem Kursplan wählen <span className="muted">(optional)</span></span><ChevronRight size={16} className="muted" />
    </button>
  );

  return (
    <div data-testid="slot-picker" className="rounded-xl bg-white border border-[#D9D5CE] p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-sm font-semibold font-head">Wann passt es dir? Wähle einen Kurs:</p>
        <button type="button" data-testid="slot-close-btn" onClick={() => setOpen(false)} className="text-xs muted hover:text-[#161616]">Schließen</button>
      </div>
      {days === null && <p data-testid="slot-loading" className="text-sm muted">Kursplan wird geladen …</p>}
      {days?.length === 0 && <p data-testid="slot-empty" className="text-sm muted">Gerade keine Termine verfügbar – schreib uns einfach deine Wunschzeit in die Nachricht.</p>}
      {days?.length > 0 && (
        <>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" role="tablist" aria-label="Tag wählen">
            {days.map((d) => (
              <button type="button" key={d.date} role="tab" aria-selected={d.date === activeDay} data-testid={`slot-day-${d.date}`} onClick={() => setActiveDay(d.date)}
                className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-semibold font-head border transition-colors ${d.date === activeDay ? "bg-[#161616] text-white border-[#161616]" : "border-[#D9D5CE] hover:border-[#161616]"}`}>{fmtDay(d.date)}</button>
            ))}
          </div>
          <ul className="mt-2 grid sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
            {items.map((i) => (
              <li key={i.id}>
                <button type="button" data-testid="slot-option" onClick={() => { onChange(slotLabel(i), i.date); setOpen(false); }} disabled={i.spots_left === 0}
                  className="w-full text-left rounded-lg border border-[#E6E3DD] hover:border-[#BA00C5] disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2.5 flex items-center gap-3 transition-colors">
                  <span className="w-1.5 h-8 rounded-full shrink-0" style={{ backgroundColor: i.color }} />
                  <span className="min-w-0"><span className="block text-xs font-semibold tabular-nums">{i.start_time} – {i.end_time}</span><span className="block text-sm font-head font-bold truncate">{findCourse(i.name)?.title || i.name}</span></span>
                  {i.spots_left !== null && i.spots_left !== undefined && <span className={`ml-auto text-xs shrink-0 ${i.spots_left === 0 ? "muted" : "text-[#047857]"}`}>{i.spots_left === 0 ? "voll" : `${i.spots_left} frei`}</span>}
                </button>
              </li>
            ))}
          </ul>
          <p data-testid="slot-note" className="mt-3 text-xs muted">Das ist noch keine Buchung – deinen Termin bestätigen wir persönlich per E-Mail.</p>
        </>
      )}
    </div>
  );
};
